import React, { useState } from "react";
import { AddCircle, Tornado } from "@mui/icons-material";
import { IconButton, Box, Typography } from "@mui/material";
import { DEPENDENCY_RFG } from "../Constants/PolicyTranslationConstants";
import PolicyRulesDataGrid from "../DataGrids/PolicyRulesDataGrid";
import ProcedureCodeDialog from "../../PolicyTranslation/Dialogs/ProcedureCodeDialog";
import ActionsFragment from "../Fragments/ActionsFragment";
import UseCasesActionsFragment from "../Fragments/UseCasesActionsFragment";
import StartDateFragment from "../Fragments/StartDateFragment";
import DuplicateRowFragment from "../Fragments/DuplicateRowFragment";
import { useLocation } from "react-router-dom";
import styled from "@emotion/styled";
import { getLobName } from "../Utils/TranslationUtils";
import {
  clearValidationErrors,
  isSameCell,
  renderCellClass,
  renderError,
  renderValidationError,
} from "../Validation/ValidationHelper";
import { validateArray, validateString } from "../Validation/Validators";
import { useGridApiRef } from "@mui/x-data-grid";
import dayjs from "dayjs";
import { showVarianceIcon } from "utils/shared";
import StyledTornadoIcon from "components/TornadoIcon";

const AddProcedureCode = styled("a")(({ theme }) => {
  return {
    textTransform: "uppercase" /* Make text uppercase */,
    color: "blue" /* Standard hyperlink color */,
    pointerEvents: "none" /* Make the link not clickable */,
    cursor: "default" /* Change cursor to default */,
  };
});

const DependencyTable = ({
  gridHeight,
  setLoading,
  updateRulesAfterEdit,
  availableProcedureCodes,
  chips,
  editable,
  rows,
  setSelectedCC,
  addNewRows,
  duplicateRow,
  selectedCC,
  handleDelete,
  policyData,
  pageComponent,
  handleRemoveInvalidRow,
  endExistingRule,
}) => {
  const [isPxDialogOpen, setIsPxDialogOpen] = useState(false);
  const [openDxDialog, setOpenDxDialog] = useState(false);
  const { state } = useLocation();
  const [validationErrors, setValidationErrors] = useState([]);
  const apiRef = useGridApiRef();

  const selectedHealthPlan = state?.policyState?.healthPlanName;

  const handleAddProcedureCode = () => {
    setIsPxDialogOpen(true);
  };

  const handleAddRowToGrid = (checkedItems) => {
    addNewRows(checkedItems, DEPENDENCY_RFG);
    setOpenDxDialog(false);
  };

  const resetCheckColor = (id) => {
    const cellElement = apiRef.current.getCellElement(id, "actions");
    if (cellElement) {
      const checkIcon = cellElement.querySelector(
        ".MuiSvgIcon-root.validateRowCheck"
      );
      if (checkIcon) {
        checkIcon.style.color = "inherit";
      }
    }
  };
  const handleValidateCell = (data, params) => {
    if (params.hasChanged) {
      resetCheckColor(params.id);
    }
    const validator = apiRef.current.getColumn(data.cellField).validator;
    validator(data);
    if (data.isValid) {
      clearValidationErrors(data, setValidationErrors);
    } else {
      const index = validationErrors.findIndex((item) =>
        isSameCell(data, item)
      );
      if (index === -1) {
        setValidationErrors((validationErrors) => [...validationErrors, data]);
      } else {
        const updatedErrors = [...validationErrors];
        updatedErrors[index] = data;
        setValidationErrors(updatedErrors);
      }
    }
  };

  const handleValidate = (params, value) => {
    const data = {
      cellField: "startDate",
      headerName: "Date & LOB Configuration",
      rowId: params.row.id,
      value: value || "",
      isValid: true,
    };
    handleValidateCell(data, params);
  };
  const columns = [
    {
      display: "flex",
      field: "CoverageCriteriaList",
      headerName: "CC#",
      flex: 0.6,
      minWidth: 125,
      editable: editable,
      cellClassName: (params) => renderCellClass(params),
      valueGetter: (value, row) => {
        return row.RuleFacts.CoverageCriteriaList?.trim() || "";
      },
      valueSetter: (value, row) => {
        let newRow = structuredClone(row);
        newRow.RuleFacts.CoverageCriteriaList = value ? value?.trim() : "";
        return newRow;
      },
      renderCell: (params) => {
        const { colDef, row } = params;
        const error = renderError(colDef.field, row);
        if (error) {
          return error;
        }
        return row.RuleFacts.CoverageCriteriaList?.trim() || "";
      },
    },
    {
      display: "flex",
      field: "procedureCode",
      headerName: "Procedure Code",
      flex: 0.8,
      minWidth: 110,
      renderCell: (params) => (
        <Box
          display="flex"
          flexDirection="row"
          justifyContent="center"
          alignItems="center"
          height="100%"
        >
          <DuplicateRowFragment
            editable={editable && pageComponent === "PolicyTranslation"}
            params={params}
            value={params.row.RuleFacts.ProcedureCode}
            duplicateRow={duplicateRow}
          />
          {/* This is used to identify variances in new rules and rules that
                      have completed the sheet generation process */}
          {editable &&
            showVarianceIcon(params.row.RuleHeader, selectedHealthPlan) && (
              <StyledTornadoIcon />
            )}
        </Box>
      ),
      valueGetter: (value, row) => row.RuleFacts.ProcedureCode,
    },
    {
      display: "flex",
      field: "DependencyProcedureCode",
      headerName: "Requires Primary Procedure Code",
      flex: 0.8,
      minWidth: 120,
      editable: editable,
      cellClassName: (params) => renderCellClass(params),
      valueGetter: (value, row) => {
        return row.RuleFacts.DependencyProcedureCode || "";
      },
      renderCell: (params) => {
        const { colDef, row } = params;
        const error = renderError(colDef.field, row);
        if (error) {
          return error;
        }
        return params.value;
      },
      valueSetter: (value, row) => {
        let newRow = structuredClone(row);
        newRow.RuleFacts.DependencyProcedureCode = value?.trim() || "";
        return newRow;
      },
    },
    {
      display: "flex",
      field: "startDate",
      headerName: "Date & LOB Configuration",
      flex: 1.1,
      minWidth: 220,
      cellClassName: (params) => renderCellClass(params),
      editable: editable,
      valueGetter: (value, row) => {
        return row.RuleFacts?.RuleEffectiveDate
          ? dayjs(row.RuleFacts?.RuleEffectiveDate).format("MM/DD/YYYY")
          : "";
      },
      renderEditCell: (params) => {
        return (
          <StartDateFragment
            updateRulesAfterEdit={updateRulesAfterEdit}
            handleRemoveInvalidRow={handleRemoveInvalidRow}
            editable={editable && pageComponent === "PolicyTranslation"}
            params={params}
            policyData={policyData}
            endExistingRule={endExistingRule}
          />
        );
      },
      renderCell: (params) => {
        const { colDef, row } = params;
        const error = renderError(colDef.field, row);
        if (error) {
          return error;
        }
        return params.value;
      },
    },
    {
      display: "flex",
      field: "LobDisplayName",
      headerName: "LOB",
      minWidth: 150,
      valueGetter: (value, row) => {
        return getLobName(row.RuleFacts.LobConfigKey, policyData);
      },
      cellClassName: (params) => renderCellClass(params),

      renderCell: (params) => {
        const { colDef, row } = params;
        const error = renderError(colDef.field, row);
        if (error) {
          return error;
        }
        return params.value || "";
      },
    },
    {
      display: "flex",
      field: "actions",
      headerName: "Actions",
      flex: 1,
      minWidth: 200,
      renderCell: (params) => {
        return pageComponent === "PolicyTranslation" ? (
          <ActionsFragment
            handleDelete={handleDelete}
            editable={editable}
            params={params}
          />
        ) : (
          <UseCasesActionsFragment
            editable={editable && pageComponent === "UseCases"}
            params={params}
          />
        );
      },
    },
  ];

  return (
    <div style={{ width: "100%" }}>
      {pageComponent !== "UseCases" && (
        <Box>
          <IconButton
            disabled={!editable}
            onClick={handleAddProcedureCode}
            color="primary"
            aria-label="add new row"
          >
            <AddCircle />
          </IconButton>
          <AddProcedureCode>Add Procedure Code</AddProcedureCode>
        </Box>
      )}
      <PolicyRulesDataGrid
        gridHeight={gridHeight}
        setLoading={setLoading}
        editable={editable && pageComponent === "PolicyTranslation"}
        rows={rows}
        apiRef={apiRef}
        columns={columns}
        initialState={{
          sorting: {
            sortModel: [{ field: "procedureCode", sort: "asc" }],
          },
        }}
        processRowUpdate={(newRow, oldRow) =>
          updateRulesAfterEdit(newRow, oldRow)
        }
      />
      <ProcedureCodeDialog
        availableProcedureCodes={availableProcedureCodes}
        selectedCC={selectedCC}
        setSelectedCC={setSelectedCC}
        chips={chips}
        open={isPxDialogOpen}
        onClose={() => setIsPxDialogOpen(false)}
        onSave={handleAddRowToGrid}
      />
    </div>
  );
};

export default DependencyTable;
